import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { JwtHelperService } from '@auth0/angular-jwt';
import { map, Observable, tap } from 'rxjs';
import { ConfirmationService } from 'primeng/api';

import { environment } from '@env/environment';
import { IResponse } from '@core/models/general';
import {
  CheckDniDto,
  CheckDniResponse,
  RegisterDto,
  SendVerificationCodeDto,
  SendVerificationCodeResponse,
  ValidateVerificationCodeDto,
  ValidateVerificationCodeResponse,
} from '@core/models/register';
import { tryCatchSync } from '@core/helpers/try-catch.helper';
import { IAuthResponse } from '@core/models/auth';
import { Router } from '@angular/router';

import { STORAGE_STRATEGY } from './storage.token';
import { AuthTokenService } from './auth-token.service';
import { AppStateService } from './app-state.service';

@Injectable({ providedIn: 'root' })
export class RegisterService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(STORAGE_STRATEGY);
  private readonly authTokenService = inject(AuthTokenService);
  private readonly appStateService = inject(AppStateService);
  private readonly router = inject(Router);
  private readonly jwtHelperService = new JwtHelperService();
  private readonly sanitizer = inject(DomSanitizer);
  private readonly confirmationService = inject(ConfirmationService);

  readonly REGISTER_TOKEN_KEY = 'register-verification-token';

  readonly url = `${environment.api.baseUrl}/register`;

  sendVerificationCode(body: SendVerificationCodeDto): Observable<SendVerificationCodeResponse> {
    return this.http
      .post<IResponse<SendVerificationCodeResponse>>(`${this.url}/send-verification-code`, body)
      .pipe(map(response => response.data));
  }

  validateVerificationCode(
    body: ValidateVerificationCodeDto
  ): Observable<ValidateVerificationCodeResponse> {
    return this.http
      .post<
        IResponse<ValidateVerificationCodeResponse>
      >(`${this.url}/validate-verification-code`, body)
      .pipe(map(response => response.data));
  }

  register(body: RegisterDto): Observable<IAuthResponse> {
    return this.http.post<IResponse<IAuthResponse>>(`${this.url}/create-account`, body).pipe(
      tap(async response => {
        this.removeRegisterVerificationToken();
        this.storage.remove('refCode');
        this.authTokenService.setAuthToken(response.data.accessToken);
        this.appStateService.loadInitialState();
        await this.router.navigate(['/'], { replaceUrl: true });
      }),
      map(response => response.data)
    );
  }

  checkDni(body: CheckDniDto): Observable<CheckDniResponse> {
    return this.http
      .post<IResponse<CheckDniResponse>>(`${this.url}/check-dni`, body)
      .pipe(map(response => response.data));
  }

  setRegisterVerificationToken(token: string): void {
    this.storage.set(this.REGISTER_TOKEN_KEY, token);
  }

  getRegisterVerificationToken(): string | null {
    return this.storage.get<string>(this.REGISTER_TOKEN_KEY);
  }

  removeRegisterVerificationToken(): void {
    this.storage.remove(this.REGISTER_TOKEN_KEY);
  }

  validateRegisterVerificationToken(): boolean {
    const token = this.storage.get<string>(this.REGISTER_TOKEN_KEY);

    const [error, isValidToken] = tryCatchSync(
      () =>
        !!token &&
        !!this.jwtHelperService.decodeToken(token) &&
        !this.jwtHelperService.isTokenExpired(token)
    );

    if (!error && isValidToken) {
      return true;
    }

    this.removeRegisterVerificationToken();
    return false;
  }

  showInvalidMessageRegisterVerificationToken(): void {
    const htmlMessage: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<p>El código de verificación es válido por un tiempo limitado. Si deseas registrarte, por favor inicia el proceso nuevamente desde la página de registro.</p>`
    );
    this.confirmationService.confirm({
      key: 'avisoDialog',
      message: htmlMessage as string,
      header: 'Aviso',
      icon: 'pi pi-exclamation-triangle',
      acceptVisible: false,
      rejectLabel: 'Aceptar',
      rejectButtonProps: { label: 'Aceptar', severity: 'secondary', outlined: true, focus: true },
    });
  }
}
